import { useCallback, useEffect, useState } from "react";
import { Box, Button, Flex, HStack, Image, Portal, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiArrowRight, FiX } from "react-icons/fi";
import { ACCENT, PAPER, SERIF } from "../theme/tokens";

/**
 * The Institute's story, as a deck — what the site is for, who it serves and
 * why it exists. Opened from the landing page by anyone who lands on us cold
 * and wants the argument before the sign-up form.
 *
 * Modelled on the lecture decks in the teaching sites: a full-screen stage,
 * arrow keys and click zones, dark slides as section dividers. Every figure
 * below is drawn from the platform itself — the survey scorecard, the tools
 * hub, the workshop list — so it stays true as those grow.
 */

const INK = "#2b2320";
const INK_SOFT = "#544842";
const DARK_BG = "linear-gradient(135deg, #7A2E2E 0%, #3f1919 100%)";

/* ---------------------------------------------------------------- blocks */

const P = ({ children, ...rest }) => (
  <Text mb="1.6vh" {...rest}>
    {children}
  </Text>
);

const UL = ({ items }) => (
  <Box as="ul" listStyleType="none" m={0} p={0}>
    {items.map((item, i) => (
      <Box as="li" key={i} position="relative" pl="1.3em" mb="1.5vh">
        <Box as="span" position="absolute" left={0} fontWeight="bold" color="inherit" opacity={0.65}>
          ▸
        </Box>
        {item}
      </Box>
    ))}
  </Box>
);

const Stat = ({ value, dark, children }) => (
  <Box mb="1.6vh">
    <Text
      fontFamily={SERIF}
      fontWeight="bold"
      lineHeight="1"
      fontSize="clamp(56px, 9vw, 128px)"
      letterSpacing="-0.03em"
      color={dark ? "white" : ACCENT}
    >
      {value}
    </Text>
    {children && (
      <Text fontSize="0.72em" opacity={0.75} mt="0.6vh">
        {children}
      </Text>
    )}
  </Box>
);

// The pull-aside that carries the "what this means for you" thread.
const Aside = ({ dark, children }) => (
  <Box
    mt="2.4vh"
    px="1.4em"
    py="1em"
    borderLeftWidth="4px"
    borderLeftColor={dark ? "rgba(255,255,255,0.55)" : ACCENT}
    borderRadius="0 10px 10px 0"
    bg={dark ? "rgba(255,255,255,0.12)" : "rgba(122,46,46,0.07)"}
    fontSize="0.72em"
    lineHeight="1.5"
  >
    {children}
  </Box>
);

const Cols = ({ children }) => (
  <Box
    display="grid"
    gridTemplateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }}
    gap={{ base: "2vh", md: "2.2vw" }}
  >
    {children}
  </Box>
);

const Col = ({ head, children }) => (
  <Box>
    <Text
      fontSize="0.62em"
      fontWeight="bold"
      letterSpacing="0.12em"
      textTransform="uppercase"
      opacity={0.7}
      mb="0.8vh"
    >
      {head}
    </Text>
    <Box fontSize="0.82em" lineHeight="1.5">
      {children}
    </Box>
  </Box>
);

const Em = ({ children }) => (
  <Box as="em" fontStyle="normal" fontWeight="semibold">
    {children}
  </Box>
);

/* ---------------------------------------------------------------- slides */

// Figures are real: 21 countries and 5,077 firms from the survey scorecard,
// seven tools from the hub, nine workshops from Lisbon 2022 to Milan 2026.
const buildSlides = (t) => [
  {
    dark: true,
    logo: true,
    photo: "/conference_6.webp",
    kicker: t("deck.k_intro", "Global Wine Business Institute"),
    title: t("deck.t_intro", "Where wine meets knowledge"),
    body: (
      <>
        <P fontSize="1.1em">
          {t(
            "deck.b_intro",
            "A digital home for the people who study, run and regulate the wine business — academics, companies and policymakers, in one place."
          )}
        </P>
        <Aside dark>
          {t(
            "deck.a_intro",
            "Free to join. Built by researchers, not a vendor. Everything here is either open evidence or work our members made together."
          )}
        </Aside>
      </>
    ),
  },
  {
    kicker: t("deck.k_problem", "The problem"),
    title: t("deck.t_problem", "The field knows a great deal, and almost none of it is in one place"),
    body: (
      <>
        <UL
          items={[
            t(
              "deck.b_problem_1",
              "Wine research is scattered across journals, languages and conferences that rarely meet."
            ),
            t(
              "deck.b_problem_2",
              "Companies hold the questions that matter and seldom see the research that answers them."
            ),
            t(
              "deck.b_problem_3",
              "Policymakers need comparable, sourced evidence — and get national statistics that stop at the border."
            ),
          ]}
        />
        <Aside>
          {t(
            "deck.a_problem",
            "None of this is a knowledge problem. It is an infrastructure problem — which is a thing you can actually build."
          )}
        </Aside>
      </>
    ),
  },
  {
    figure: "/conference_15.webp",
    kicker: t("deck.k_survey", "What we built first"),
    title: t("deck.t_survey", "A firm-level picture of how the world makes wine"),
    body: (
      <>
        <Stat value="5,077" dark={false}>
          {t("deck.b_survey_stat", "wineries surveyed across 21 wine-producing countries")}
        </Stat>
        <P>
          {t(
            "deck.b_survey",
            "Not national totals — individual firms, asked the same questions: what they export, what they earn, how they price, whether they farm organically, and how much their climate has already changed."
          )}
        </P>
        <Aside>
          {t(
            "deck.a_survey",
            "It is the part of the picture the trade statistics cannot show you, and it is open on the site as an interactive atlas."
          )}
        </Aside>
      </>
    ),
  },
  {
    dark: true,
    photo: "/conference_9.webp",
    kicker: t("deck.k_div_build", "What the site does"),
    title: t("deck.t_div_build", "Four things, one membership"),
    body: (
      <P fontSize="1.05em">
        {t(
          "deck.b_div_build",
          "An evidence base, a set of research tools, a community, and a way for industry and academia to actually work on the same question."
        )}
      </P>
    ),
  },
  {
    kicker: t("deck.k_obs", "1 · The Observatory"),
    title: t("deck.t_obs", "Evidence you can cite"),
    body: (
      <>
        <UL
          items={[
            t(
              "deck.b_obs_1",
              "Country portraits — exports, organic share, premiumisation and warming, for every surveyed country."
            ),
            t("deck.b_obs_2", "Head-to-head comparison of any two countries on the same measures."),
            t(
              "deck.b_obs_3",
              "An interactive world atlas, shaded by whichever measure you are arguing about."
            ),
          ]}
        />
        <Aside>
          {t(
            "deck.a_obs",
            "Descriptive, sourced and citable — written to be quoted in a paper, a board pack or a policy submission."
          )}
        </Aside>
      </>
    ),
  },
  {
    kicker: t("deck.k_tools", "2 · Co-creation tools"),
    title: t("deck.t_tools", "Seven tools that turn a question into work"),
    body: (
      <>
        <UL
          items={[
            <>
              <Em>{t("deck.b_tools_1_h", "Reverse Seminars")}</Em>
              {t(
                "deck.b_tools_1",
                " — a company posts a real problem, and researchers answer it. The brief runs the other way round."
              )}
            </>,
            <>
              <Em>{t("deck.b_tools_2_h", "Ask the Expert")}</Em>
              {t("deck.b_tools_2", " — a short question to the whole network, not to one inbox.")}
            </>,
            <>
              <Em>{t("deck.b_tools_3_h", "Project Builder & Data Forum")}</Em>
              {t(
                "deck.b_tools_3",
                " — find collaborators, agree a method, and keep the working papers in one thread."
              )}
            </>,
          ]}
        />
        <Aside>
          {t(
            "deck.a_tools",
            "Plus a visualisation builder, an event creator and a paper-to-brief generator — because a finding nobody reads is not a finding."
          )}
        </Aside>
      </>
    ),
  },
  {
    figure: "/conference_5.webp",
    kicker: t("deck.k_network", "3 · The network"),
    title: t("deck.t_network", "The people, and how to reach them"),
    body: (
      <>
        <UL
          items={[
            t(
              "deck.b_network_1",
              "Member profiles with a readable public address, so your work has somewhere to point."
            ),
            t(
              "deck.b_network_2",
              "Directories of academics, wineries, institutions and strategic partners — filtered by what they actually do."
            ),
            t(
              "deck.b_network_3",
              "Discussions, regional clusters and thematic groups, in seven languages."
            ),
          ]}
        />
        <Aside>
          {t(
            "deck.a_network",
            "Nine workshops so far, Lisbon 2022 through Milan 2026 — the site is where that conversation lives between them."
          )}
        </Aside>
      </>
    ),
  },
  {
    kicker: t("deck.k_feed", "4 · Staying current"),
    title: t("deck.t_feed", "The field, gathered for you each week"),
    body: (
      <UL
        items={[
          t(
            "deck.b_feed_1",
            "A newswire and a journal watch, pulled from the sources that matter and summarised."
          ),
          t(
            "deck.b_feed_2",
            "An events calendar covering conferences, fairs, shows and judgings worldwide."
          ),
          t(
            "deck.b_feed_3",
            "A weekly digest, so the reading finds you instead of the other way round."
          ),
        ]}
      />
    ),
  },
  {
    dark: true,
    kicker: t("deck.k_div_why", "Why it is built this way"),
    title: t("deck.t_div_why", "Knowledge has to travel in both directions"),
    body: (
      <P fontSize="1.05em">
        {t(
          "deck.b_div_why",
          "Research that reaches industry as a strategy. Industry problems that reach research as an agenda. Most platforms only do the first half."
        )}
      </P>
    ),
  },
  {
    kicker: t("deck.k_cocreate", "Co-creation"),
    title: t("deck.t_cocreate", "The brief runs both ways"),
    body: (
      <>
        <Cols>
          <Col head={t("deck.c_cocreate_1_h", "Academia → industry")}>
            {t(
              "deck.c_cocreate_1",
              "Findings translated into something a commercial director can act on this quarter."
            )}
          </Col>
          <Col head={t("deck.c_cocreate_2_h", "Industry → academia")}>
            {t(
              "deck.c_cocreate_2",
              "Real constraints and real evidence shaping which questions get asked at all."
            )}
          </Col>
          <Col head={t("deck.c_cocreate_3_h", "Both → policy")}>
            {t(
              "deck.c_cocreate_3",
              "Comparable evidence, across borders, for the people writing the rules."
            )}
          </Col>
        </Cols>
        <Aside>
          {t(
            "deck.a_cocreate",
            "This is what the Reverse Seminars are for: the company sets the question, the network answers it in the open."
          )}
        </Aside>
      </>
    ),
  },
  {
    kicker: t("deck.k_who", "Who it is for"),
    title: t("deck.t_who", "Three audiences, one evidence base"),
    body: (
      <Cols>
        <Col head={t("deck.c_who_1_h", "Academics")}>
          {t(
            "deck.c_who_1",
            "Evidence to work with, collaborators to work alongside, and a profile that makes the work findable."
          )}
        </Col>
        <Col head={t("deck.c_who_2_h", "Companies")}>
          {t(
            "deck.c_who_2",
            "Benchmarks against 5,000 peer firms, and researchers who will take your problem seriously."
          )}
        </Col>
        <Col head={t("deck.c_who_3_h", "Policymakers")}>
          {t(
            "deck.c_who_3",
            "Cross-border comparisons on sustainability, trade and climate that stop at no national border."
          )}
        </Col>
      </Cols>
    ),
  },
  {
    figure: "/conference_17.webp",
    kicker: t("deck.k_sustain", "The long argument"),
    title: t("deck.t_sustain", "A sector with a climate problem it cannot outrun"),
    body: (
      <>
        <P>
          {t(
            "deck.b_sustain",
            "Wine is agriculture on a fifty-year horizon: a vine planted now is a bet on a climate nobody has measured yet. The survey already records how much each region has warmed since the 1980s."
          )}
        </P>
        <Aside>
          {t(
            "deck.a_sustain",
            "Championing sustainable and responsible practice is not a section of the site. It is most of the reason for building it."
          )}
        </Aside>
      </>
    ),
  },
  {
    kicker: t("deck.k_open", "How it is run"),
    title: t("deck.t_open", "Free, open, and built by the people who use it"),
    body: (
      <UL
        items={[
          t("deck.b_open_1", "A free account. No paywall in front of the evidence."),
          t(
            "deck.b_open_2",
            "Built and maintained by academics, not sold by a vendor with a roadmap of its own."
          ),
          t(
            "deck.b_open_3",
            "Everything published is sourced, and the survey method is documented on the site."
          ),
        ]}
      />
    ),
  },
  // --- For a partner institution weighing an MOU: who runs this, what it has
  // --- already done, and what signing actually means on both sides.
  {
    dark: true,
    kicker: t("deck.k_div_partner", "Working with the Institute"),
    title: t("deck.t_div_partner", "What a partnership involves"),
    body: (
      <P fontSize="1.05em">
        {t(
          "deck.b_div_partner",
          "The Institute works through partner institutions — universities, research centres, associations and companies — each represented in its own region and field."
        )}
      </P>
    ),
  },
  {
    kicker: t("deck.k_governance", "Governance"),
    title: t("deck.t_governance", "Who runs the Institute"),
    body: (
      <>
        <UL
          items={[
            <>
              <Em>{t("deck.b_gov_1_h", "Governing Board")}</Em>
              {t("deck.b_gov_1", " — sets the long-term strategic direction and is accountable for overall leadership.")}
            </>,
            <>
              <Em>{t("deck.b_gov_2_h", "Executive Board")}</Em>
              {t("deck.b_gov_2", " — runs the Institute day to day, coordinating programmes and working groups across regions and themes.")}
            </>,
            <>
              <Em>{t("deck.b_gov_3_h", "Industry Advisory Board")}</Em>
              {t("deck.b_gov_3", " — brings practitioner expertise from across the sector, to keep the work grounded.")}
            </>,
            <>
              <Em>{t("deck.b_gov_4_h", "Regional Managers & Coordinators")}</Em>
              {t("deck.b_gov_4", " — one team per world region, representing and growing the community there.")}
            </>,
            <>
              <Em>{t("deck.b_gov_5_h", "Theme Leaders")}</Em>
              {t("deck.b_gov_5", " — four per theme, curating the research, content and events in each knowledge domain.")}
            </>,
          ]}
        />
        <Aside>
          {t(
            "deck.a_governance",
            "Every board and regional team is published on the site, with named members — governance is public, not a page in a prospectus."
          )}
        </Aside>
      </>
    ),
  },
  {
    figure: "/conference_9.webp",
    kicker: t("deck.k_track", "Track record"),
    title: t("deck.t_track", "Nine workshops, four continents, since 2022"),
    body: (
      <>
        <UL
          items={[
            t("deck.b_track_1", "Lisbon 2022 · Mendoza 2023 · Connecticut 2024 · Geneva 2024"),
            t("deck.b_track_2", "Valparaíso 2024 · Zaragoza 2025 · Cape Town 2025"),
            t("deck.b_track_3", "Perth 2026 · Milan 2026"),
          ]}
        />
        <Aside>
          {t(
            "deck.a_track",
            "Each workshop brings academics, companies and policymakers into the same room. The platform is where that conversation continues between them."
          )}
        </Aside>
      </>
    ),
  },
  {
    kicker: t("deck.k_mou", "The agreement"),
    title: t("deck.t_mou", "What each side brings"),
    body: (
      <>
        <Cols>
          <Col head={t("deck.c_mou_1_h", "The Institute provides")}>
            {t(
              "deck.c_mou_1",
              "Access to the survey evidence and research tools, a named seat in regional and thematic governance, and a platform for your researchers and members."
            )}
          </Col>
          <Col head={t("deck.c_mou_2_h", "The partner provides")}>
            {t(
              "deck.c_mou_2",
              "Regional representation, researchers or practitioners who take part in the workshops and working groups, and participation in the survey — which the Institute runs and maintains itself."
            )}
          </Col>
          <Col head={t("deck.c_mou_3_h", "Neither side gives up")}>
            {t(
              "deck.c_mou_3",
              "Independence. No fee, no exclusivity, no claim on the partner's own data or publications."
            )}
          </Col>
        </Cols>
        <Aside>
          {t(
            "deck.a_mou",
            "The specific commitments are set out in the MOU itself; this is the shape of the relationship it formalises."
          )}
        </Aside>
      </>
    ),
  },
  {
    dark: true,
    photo: "/conference_5.webp",
    kicker: t("deck.k_join", "Join us"),
    title: t("deck.t_join", "Bring your question"),
    body: (
      <>
        <P fontSize="1.05em">
          {t(
            "deck.b_join",
            "Bring a question, run the tools, post a challenge, or simply read. The survey takes one sitting, and keeping it current is our work, not yours. The network is worth what its members put into it."
          )}
        </P>
        <Aside dark>
          {t("deck.a_join", "globalwineinstitute.org — create a free account and start anywhere.")}
        </Aside>
      </>
    ),
    cta: true,
  },
];

/* ------------------------------------------------------------------ deck */

export default function StoryDeck({ isOpen, onClose }) {
  const { t } = useTranslation("common");
  const [idx, setIdx] = useState(0);
  const slides = buildSlides(t);
  const last = slides.length - 1;
  const slide = slides[idx];

  const go = useCallback(
    (delta) => setIdx((i) => Math.max(0, Math.min(last, i + delta))),
    [last]
  );

  // Always reopen at the beginning: a returning visitor wants the argument
  // from the top, not wherever they abandoned it last time.
  useEffect(() => {
    if (isOpen) setIdx(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        go(1);
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        go(-1);
      } else if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Home") {
        setIdx(0);
      } else if (e.key === "End") {
        setIdx(last);
      }
    };
    document.addEventListener("keydown", onKey);
    // The page behind must not scroll while the deck is over it.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, go, last, onClose]);

  if (!isOpen) return null;

  const dark = !!slide.dark;

  // Portalled to the body: the deck is also opened from inside the
  // dashboard sidebar, whose ancestors form their own stacking context —
  // a fixed child cannot escape one however high its z-index, and the
  // feed drew straight over it.
  return (
    <Portal>
      <Box
        position="fixed"
        inset={0}
        zIndex={2000}
        display="flex"
        flexDirection="column"
        bg={dark ? "transparent" : PAPER}
        bgImage={dark ? DARK_BG : undefined}
        color={dark ? "white" : INK}
        sx={
          // A photograph behind a dark slide, under a bordeaux scrim heavy
          // enough that the text keeps its contrast whatever the picture does.
          slide.photo && dark
            ? {
                backgroundImage: `linear-gradient(135deg, rgba(122,46,46,0.88) 0%, rgba(63,25,25,0.94) 100%), url(${slide.photo})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
        userSelect="none"
        role="dialog"
        aria-modal="true"
        aria-label={t("deck.aria", "About the Global Wine Business Institute")}
      >
        {/* stage */}
        <Flex
          flex="1"
          align="center"
          justify="center"
          px={{ base: 6, md: "7vw" }}
          py={{ base: 8, md: "5vh" }}
          position="relative"
          overflowY="auto"
        >
          {/* click zones, as in the teaching decks — tap anywhere to advance */}
          <Box
            position="absolute"
            insetY={0}
            left={0}
            w="28%"
            zIndex={1}
            cursor={idx > 0 ? "pointer" : "default"}
            onClick={() => go(-1)}
            aria-hidden
          />
          <Box
            position="absolute"
            insetY={0}
            right={0}
            w="44%"
            zIndex={1}
            cursor={idx < last ? "pointer" : "default"}
            onClick={() => go(1)}
            aria-hidden
          />

          <Box
            maxW={slide.figure ? "1240px" : "1100px"}
            w="100%"
            position="relative"
            zIndex={2}
            pointerEvents="none"
            display={slide.figure ? { base: "block", lg: "grid" } : undefined}
            gridTemplateColumns={slide.figure ? "1.15fr 0.85fr" : undefined}
            gap={slide.figure ? "3vw" : undefined}
            alignItems={slide.figure ? "center" : undefined}
          >
            <Box>
            {slide.logo && (
              <Image
                src="/gwbi_logo_red.png"
                alt=""
                h={{ base: "70px", md: "100px" }}
                mb="3vh"
                sx={{ filter: "brightness(0) invert(1)" }}
                fallbackSrc="data:,"
              />
            )}
            <Text
              fontSize="clamp(11px, 1.2vw, 15px)"
              letterSpacing="0.28em"
              textTransform="uppercase"
              fontWeight="bold"
              color={dark ? "whiteAlpha.800" : ACCENT}
              mb="1.8vh"
            >
              {slide.kicker}
            </Text>
            <Box
              as="h2"
              fontFamily={SERIF}
              fontWeight="bold"
              fontSize="clamp(30px, 4.4vw, 60px)"
              lineHeight="1.08"
              letterSpacing="-0.02em"
              mb="3vh"
            >
              {slide.title}
            </Box>
            <Box
              fontSize="clamp(16px, 1.85vw, 25px)"
              lineHeight="1.5"
              maxW="62ch"
              color={dark ? "whiteAlpha.900" : INK_SOFT}
            >
              {slide.body}
              </Box>
              {slide.cta && (
                  <HStack spacing={3} mt="3.5vh" pointerEvents="auto" flexWrap="wrap">
                    <Button
                      as={RouterLink}
                      to="/register"
                      size="lg"
                      bg="white"
                      color={ACCENT}
                      _hover={{ bg: "gray.100" }}
                      onClick={onClose}
                    >
                      {t("deck.cta_join", "Create a free account")}
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      color="white"
                      borderColor="whiteAlpha.700"
                      _hover={{ bg: "whiteAlpha.200", borderColor: "white" }}
                      onClick={onClose}
                    >
                      {t("deck.cta_browse", "Keep looking around")}
                    </Button>
                  </HStack>
                )}
            </Box>
            {slide.figure && (
              <Image
                src={slide.figure}
                alt=""
                w="100%"
                h={{ base: "22vh", lg: "58vh" }}
                objectFit="cover"
                borderRadius="14px"
                mt={{ base: "3vh", lg: 0 }}
                boxShadow="0 18px 50px -24px rgba(43,35,32,0.55)"
              />
            )}
          </Box>
        </Flex>

        {/* chrome */}
        <Flex
          flex="none"
          align="center"
          gap={3}
          px={{ base: 4, md: 6 }}
          py={3}
          borderTopWidth="1px"
          borderColor={dark ? "whiteAlpha.300" : "border.default"}
        >
          <Text
            fontSize="11px"
            letterSpacing="0.14em"
            textTransform="uppercase"
            fontWeight="bold"
            opacity={0.65}
            flex="1"
            noOfLines={1}
          >
            {t("deck.footer", "Global Wine Business Institute")}
          </Text>

          {/* progress: one tick per slide, clickable */}
          <HStack spacing="3px" display={{ base: "none", md: "flex" }}>
            {slides.map((s, i) => (
              <Box
                key={i}
                as="button"
                onClick={() => setIdx(i)}
                aria-label={`${i + 1}`}
                w={i === idx ? "22px" : "10px"}
                h="4px"
                borderRadius="2px"
                transition="width 0.2s ease, opacity 0.2s ease"
                bg={dark ? "white" : ACCENT}
                opacity={i === idx ? 1 : 0.25}
              />
            ))}
          </HStack>

          <Button
            size="xs"
            variant="ghost"
            color="inherit"
            onClick={() => go(-1)}
            isDisabled={idx === 0}
            leftIcon={<FiArrowLeft />}
            _hover={{ bg: dark ? "whiteAlpha.200" : "blackAlpha.100" }}
          >
            {t("deck.prev", "Back")}
          </Button>
          <Button
            size="xs"
            variant={idx === last ? "ghost" : "solid"}
            bg={idx === last ? "transparent" : dark ? "white" : ACCENT}
            color={idx === last ? "inherit" : dark ? ACCENT : "white"}
            onClick={() => go(1)}
            isDisabled={idx === last}
            rightIcon={<FiArrowRight />}
            _hover={{ opacity: 0.85 }}
          >
            {t("deck.next", "Next")}
          </Button>
          <Text fontSize="12px" fontWeight="bold" minW="52px" textAlign="right" opacity={0.8}>
            {idx + 1} / {slides.length}
          </Text>
          <Button
            size="xs"
            variant="ghost"
            color="inherit"
            onClick={onClose}
            leftIcon={<FiX />}
            _hover={{ bg: dark ? "whiteAlpha.200" : "blackAlpha.100" }}
          >
            {t("deck.close", "Close")}
          </Button>
        </Flex>
      </Box>
    </Portal>
  );
}
